import { authApi } from '@/api/auth';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton, useSidebar } from '@/components/ui/sidebar';
import { useAuthStore } from '@/stores/auth';
import { BookOpen, ChevronsUpDown, Download, LogOut, Settings, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LangSwitch } from './LangSwitch';
import { ThemeMenu } from './ThemeMenu';

/**
 * The account menu, rendered once and reused by both triggers (the sidebar
 * footer and the topbar avatar) so the two can never drift apart.
 */
function AccountMenuItems() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const isAdmin = !!(user?.is_super_admin || user?.company_admin);

  async function onLogout() {
    try {
      await authApi.logout();
    } catch {
      /* tolerate — clear locally either way */
    }
    clear();
    navigate('/login', { replace: true });
  }

  return (
    <>
      <DropdownMenuLabel className="font-normal">
        <div className="flex flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-foreground">
            {user?.display_name ?? '—'}
          </span>
          <span className="truncate text-2xs text-muted-foreground">{user?.email ?? ''}</span>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => window.open('/docs', '_blank', 'noopener,noreferrer')}>
        <BookOpen aria-hidden />
        {t('public.nav_docs')}
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => navigate('/download')}>
        <Download aria-hidden />
        {t('public.nav_download')}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => navigate('/account')}>
        <Settings aria-hidden />
        {t('nav.account')}
      </DropdownMenuItem>
      {isAdmin && (
        <DropdownMenuItem onSelect={() => navigate('/admin')}>
          <ShieldCheck aria-hidden />
          {t('nav.admin')}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem variant="destructive" onSelect={() => void onLogout()}>
        <LogOut aria-hidden />
        {t('common.sign_out')}
      </DropdownMenuItem>
    </>
  );
}

/**
 * Sidebar footer: the account, as one more rail row.
 *
 * It is a `SidebarMenuButton` rather than a bespoke button so the footer hovers,
 * focuses and collapses to an avatar exactly like every row above it.
 */
export function UserMenu({ roleLabel }: { roleLabel?: string }) {
  const user = useAuthStore((s) => s.user);
  const { isMobile } = useSidebar();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
          <UserAvatar
            name={user?.display_name ?? ''}
            src={user?.avatar_url}
            className="size-8 shrink-0 rounded-lg"
            fallbackClassName="rounded-lg bg-brand text-xs font-semibold text-primary-foreground"
          />
          <div className="grid min-w-0 flex-1 text-left leading-tight">
            <span className="truncate text-sm font-semibold">{user?.display_name ?? '—'}</span>
            <span className="truncate text-xs text-muted-foreground">
              {roleLabel ?? user?.email ?? ''}
            </span>
          </div>
          <ChevronsUpDown aria-hidden className="ms-auto size-4 shrink-0" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      {/* The reference's placement: out to the side on desktop, under the
          trigger on a phone, and exactly as wide as the row it came from. */}
      <DropdownMenuContent
        side={isMobile ? 'bottom' : 'right'}
        align={isMobile ? 'start' : 'end'}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
      >
        <AccountMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HeaderRight() {
  return (
    <div className="flex items-center gap-2">
      <ThemeMenu />
      <LangSwitch />
    </div>
  );
}

/** Compact avatar trigger for the topbar; opens the same menu as the sidebar. */
export function TopbarUserMenu() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('nav.account')}
          title={user?.display_name ?? ''}
          className="relative rounded-full"
        >
          <UserAvatar
            name={user?.display_name ?? ''}
            src={user?.avatar_url}
            className="size-8"
            fallbackClassName="bg-brand text-xs font-semibold text-primary-foreground"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <AccountMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
